import { useTranslation } from 'react-i18next';
import { FieldArray, formValueSelector, reduxForm } from 'redux-form';
import { useState } from 'react';
import type { FC, FormEvent } from 'react';
import { useSelector } from 'react-redux';
import FileUpload from '../../legacy/components/ui/forms/FileUpload';
import { filesRequired, required } from '../../../core/legacy/utils/validation';
import { ButtonsContainer, ValidateButton } from '../../legacy/components/ui/modals/styles';
import { StyledForm } from '../../legacy/components/ui/forms/styles';
import { UploadSessionsCommand } from '../../../core/uploads/commands/UploadSessionsCommand';
import { CheckDuplicateFilesCommand } from '../../../core/uploads/commands/CheckDuplicateFilesCommand';
import type { UploadableSession } from '../../../core/uploads/state';
import { useAppDispatch } from '../../../types';

// react-dropzone augments the browser File with a `path`, so a selected file is both a File and an UploadableSession.
type SelectedFile = File & UploadableSession;

export const MAX_UPLOAD_SESSION_SIZE = 13000000000; // slightly more than strictly 12 GiB

const FORM_NAME = 'upload-sessions';
const selectSessions = formValueSelector(FORM_NAME);

type Props = {
    invalid: boolean;
};

const UploadSessionForm: FC<Props> = ({ invalid }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const sessions = (useSelector((state) => selectSessions(state, 'sessions')) ?? []) as SelectedFile[];

    const [duplicateFileKeys, setDuplicateFileKeys] = useState<string[]>([]);
    const [checkedKeys, setCheckedKeys] = useState<Set<string>>(() => new Set());

    const fileKey = (file: SelectedFile) => `${file.name}_${file.size}`;
    const currentKeys = sessions.map(fileKey);
    const currentKeySet = new Set(currentKeys);

    const hasUncheckedFiles = currentKeys.some((key) => !checkedKeys.has(key));
    const visibleDuplicateKeys = duplicateFileKeys.filter((key) => currentKeySet.has(key));
    const awaitingConfirmation = !hasUncheckedFiles && visibleDuplicateKeys.length > 0;

    const fileSize = (files: File[]) => {
        const isAFileTooBig = files.some((file) => file.size > MAX_UPLOAD_SESSION_SIZE);

        return isAFileTooBig ? 'error.fileSize' : null;
    };

    const onImport = async () => {
        if (hasUncheckedFiles) {
            const duplicateKeys = await dispatch(CheckDuplicateFilesCommand(sessions)).unwrap();
            setDuplicateFileKeys(duplicateKeys);
            setCheckedKeys(new Set(currentKeys));

            if (duplicateKeys.length > 0) {
                return;
            }
        }

        dispatch(UploadSessionsCommand(sessions));
    };

    return (
        <StyledForm onSubmit={(event: FormEvent) => event.preventDefault()}>
            <FieldArray
                component={FileUpload}
                name="sessions"
                props={{
                    accept: {
                        'application/x-zip': [],
                        'application/x-zip-compressed': [],
                        'application/zip': ['.zip'],
                        'application/octet-stream': [],
                        'video/mp4': ['.mp4', '.MP4'],
                    },
                    multiple: true,
                    maxSize: MAX_UPLOAD_SESSION_SIZE,
                    extraInfo: t('uploadSession.extraInfo'),
                    description: 'uploadFile.descriptionMultipleZIP',
                    duplicateFileKeys: visibleDuplicateKeys,
                }}
                validate={[required, filesRequired, fileSize]}
            />
            <ButtonsContainer>
                <ValidateButton disabled={invalid} type="button" onClick={onImport}>
                    {awaitingConfirmation ? t('uploadSession.importAnyway') : t('uploadFile.submit')}
                </ValidateButton>
            </ButtonsContainer>
        </StyledForm>
    );
};

export default reduxForm({
    form: FORM_NAME,
})(UploadSessionForm);
